import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, ShieldCheck } from "lucide-react";
import { BRAND } from "@/lib/design";

export default function Footer() {
    const year = new Date().getFullYear();
    return (
        <footer
            className="bg-[#0B1929] text-[#cfd6df] mt-24"
            data-testid="site-footer"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-5">
                    <div className="flex items-center gap-3 mb-5">
                        <img
                            src={BRAND.logo}
                            alt="Crossroads to Change Residential Treatment Center"
                            className="h-14 w-14 object-contain"
                            data-testid="footer-logo-img"
                        />
                        <span className="flex flex-col leading-tight">
                            <span className="font-[Outfit] text-[17px] font-semibold tracking-tight text-white">
                                Crossroads to Change
                            </span>
                            <span className="text-[10.5px] tracking-[0.18em] font-semibold uppercase text-[#C8A24A]">
                                Residential Treatment Center
                            </span>
                        </span>
                    </div>
                    <p className="text-[15px] leading-relaxed text-[#a5b1c0] max-w-md">
                        A trauma-informed residential treatment center
                        serving male youth in Texas foster care — providing
                        safety, structure, clinical care, and a path toward
                        healing and accountability.
                    </p>
                    <p className="mt-4 text-[13.5px] text-[#a5b1c0]">
                        Serving the Tomball Area
                    </p>
                    <div className="mt-6 flex items-center gap-3">
                        <a aria-label="Facebook" href="#" className="h-9 w-9 rounded-full border border-white/10 grid place-items-center text-[#a5b1c0] hover:text-white hover:border-[#C8A24A] transition-colors" data-testid="social-facebook">
                            <Facebook className="h-4 w-4" />
                        </a>
                        <a aria-label="Instagram" href="#" className="h-9 w-9 rounded-full border border-white/10 grid place-items-center text-[#a5b1c0] hover:text-white hover:border-[#C8A24A] transition-colors" data-testid="social-instagram">
                            <Instagram className="h-4 w-4" />
                        </a>
                        <a aria-label="LinkedIn" href="#" className="h-9 w-9 rounded-full border border-white/10 grid place-items-center text-[#a5b1c0] hover:text-white hover:border-[#C8A24A] transition-colors" data-testid="social-linkedin">
                            <Linkedin className="h-4 w-4" />
                        </a>
                    </div>
                </div>

                <div className="md:col-span-3">
                    <h4 className="font-[Outfit] text-white text-[15px] mb-4">
                        Explore
                    </h4>
                    <ul className="space-y-2.5 text-[14px] text-[#a5b1c0]">
                        <li><Link to="/" className="hover:text-white" data-testid="footer-home">Home</Link></li>
                        <li><Link to="/about" className="hover:text-white" data-testid="footer-about">About</Link></li>
                        <li><Link to="/program" className="hover:text-white" data-testid="footer-program">Our Program</Link></li>
                        <li><Link to="/who-we-serve" className="hover:text-white" data-testid="footer-who">Who We Serve</Link></li>
                        <li><Link to="/referrals" className="hover:text-white" data-testid="footer-referrals">Referrals</Link></li>
                        <li><Link to="/contact" className="hover:text-white" data-testid="footer-contact">Contact</Link></li>
                        <li><Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <h4 className="font-[Outfit] text-white text-[15px] mb-4">
                        Reach Us
                    </h4>
                    <ul className="space-y-3 text-[14px]">
                        <li className="flex items-start gap-3">
                            <MapPin className="h-4 w-4 mt-0.5 text-[#C8A24A]" />
                            <span>{BRAND.serviceArea}</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <Phone className="h-4 w-4 mt-0.5 text-[#C8A24A]" />
                            <a href={BRAND.phoneHref} className="font-bold text-[#C8A24A] hover:text-white transition-colors" data-testid="footer-phone">
                                {BRAND.phone}
                            </a>
                        </li>
                        <li className="flex items-start gap-3">
                            <Mail className="h-4 w-4 mt-0.5 text-[#C8A24A]" />
                            <a href={`mailto:${BRAND.email}`} className="hover:text-white break-all" data-testid="footer-email">
                                {BRAND.email}
                            </a>
                        </li>
                        <li className="flex items-start gap-3 pt-2">
                            <ShieldCheck className="h-4 w-4 mt-0.5 text-[#C8A24A]" />
                            <div>
                                <p className="text-white text-[12.5px] font-medium">Compliance</p>
                                <p className="text-[12px] text-[#a5b1c0] mt-0.5">
                                    Federal Tax ID (EIN): <span className="text-white">{BRAND.ein}</span><br/>
                                    Operating in compliance with applicable residential care standards.
                                </p>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-[#1f2a3a]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[12.5px] text-[#5a6778]">
                    <p>
                        © {year} {BRAND.name}. All rights reserved.
                    </p>
                    <p>
                        In a crisis? Call <a href="tel:911" className="underline hover:text-white">911</a> · Mental health support <a href="tel:988" className="underline hover:text-white">988</a>
                    </p>
                </div>
            </div>
        </footer>
    );
}
